import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import type { Action } from 'redux-actions';
import * as UserManagementActions from 'app/actions/userManagement/userManagementActions.ts';
import type UserManaged from '../../../models/userManagement/userManaged.ts';
import type { EditUserCommand, UserDto } from '@/api-gen/api.schemas';
import { accountEditUser, userGetUsers, userDeleteUser, companyGetInvitations } from '@/api-gen/api.ts';
import * as UserManagementSelectors from 'app/selectors/userManagementSelectors.ts';
import logger from 'app/utils/logger.ts';

function* getUsers() {
  yield put(UserManagementActions.setUsersLoaded(false));
  try {
    const response: Awaited<ReturnType<typeof userGetUsers>> = yield call(userGetUsers);

    yield put(UserManagementActions.setUsers(response.data as unknown as UserManaged[]));
  } catch (error) {
    logger.log(error);
  } finally {
    yield put(UserManagementActions.setUsersLoaded(true));
  }
}

function* getInvitations() {
  try {
    const response: Awaited<ReturnType<typeof companyGetInvitations>> = yield call(companyGetInvitations);
    yield put(UserManagementActions.setInvitations(response.data));
  } catch (error) {
    logger.log(error);
  }
}

function* editUser(action: Action<{ user: UserManaged; onSuccess?: () => void; onError?: (error?: unknown) => void }>) {
  const { user: userData, onSuccess, onError } = action.payload;

  yield put(UserManagementActions.setUsersLoaded(false));

  try {
    const targetUser: UserManaged = yield select(UserManagementSelectors.userById(userData.id));

    const payload: EditUserCommand = {
      id: userData.id,
      role: userData.role ?? targetUser?.role,
      phone: userData.phone ?? targetUser?.phone,
      departmentId: userData.departmentId || null,
      name: userData.name,
      hideScenarioContent: userData.hideScenarioContent,
      hasSklls: userData.hasSklls,
      hasInterpreter: userData.hasInterpreter,
      hasExercises: userData.hasExercises
    };

    const response = yield call(accountEditUser, payload);

    yield put(UserManagementActions.userUpdated(response.data));

    onSuccess?.();
  } catch (error) {
    // Pass full error to callback for 409 handling
    onError?.(error);
    logger.log(error);
  } finally {
    yield put(UserManagementActions.setUsersLoaded(true));
  }
}

function* deleteUser(action: Action<{ id: number; onSuccess?: () => void }>) {
  const { id, onSuccess } = action.payload;

  yield put(UserManagementActions.setUsersLoaded(false));

  try {
    yield call(userDeleteUser, id);

    yield put(UserManagementActions.userDeleted(id));

    onSuccess?.();
  } catch (error) {
    logger.log(error);
  } finally {
    // Always refetch to reconcile UI with DB state
    yield put(UserManagementActions.getUsers());
  }
}

function* watchGetUsers() {
  yield takeLatest(UserManagementActions.Type.GET_USERS, getUsers);
}

function* watchGetInvitations() {
  yield takeLatest(UserManagementActions.Type.GET_INVITATIONS, getInvitations);
}

function* watchEditUser() {
  yield takeLatest(UserManagementActions.Type.EDIT_USER, editUser);
}

function* watchDeleteUser() {
  yield takeLatest(UserManagementActions.Type.DELETE_USER, deleteUser);
}

export function* watchUserManagement() {
  yield fork(watchGetUsers);
  yield fork(watchGetInvitations);
  yield fork(watchEditUser);
  yield fork(watchDeleteUser);
}
